// Comprobante compacto de PAGO DE VACACIONES (cuando se pagan en lugar de disfrutarlas).
// Formato media carta, misma paleta que el comprobante de Aguinaldo.
import jsPDF from "jspdf";
import { DEJAVU_REGULAR_BASE64, DEJAVU_BOLD_BASE64 } from "@/lib/boletaFont";

const FONT_FAMILY = "NomyProSans";

const COLOR_TITLE_BG = [220, 228, 235];
const COLOR_LABEL_BG = [232, 238, 244];
const COLOR_VALUE_BG = [247, 250, 253];
const COLOR_TEXT = [30, 38, 45];
const COLOR_MUTED = [120, 130, 140];
const COLOR_BORDER = [180, 190, 200];
const COLOR_ACCENT = [70, 100, 130];

const fmtCRC = (n) => "₡" + new Intl.NumberFormat("es-CR", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(Number(n) || 0);

const fmtFechaLarga = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" });
};

function registerFont(doc) {
  doc.addFileToVFS("NomyProSans-Regular.ttf", DEJAVU_REGULAR_BASE64);
  doc.addFont("NomyProSans-Regular.ttf", FONT_FAMILY, "normal");
  doc.addFileToVFS("NomyProSans-Bold.ttf", DEJAVU_BOLD_BASE64);
  doc.addFont("NomyProSans-Bold.ttf", FONT_FAMILY, "bold");
}

function detectImageFormat(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  const m = dataUrl.match(/^data:image\/(png|jpeg|jpg|webp);base64,/i);
  if (!m) return null;
  return m[1].toLowerCase() === "jpg" ? "JPEG" : m[1].toUpperCase();
}

export function generarComprobanteVacacionesPagadas({ empresa = {}, empleado = {}, fechaPago, diasPagados, monto, motivo = "" }) {
  // Media carta (para que sea "pequeño" como pidió el usuario)
  const doc = new jsPDF({ unit: "mm", format: [140, 216] });
  registerFont(doc);
  doc.setFont(FONT_FAMILY, "normal");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  let y = margin;

  // === CABECERA EMPRESA ===
  doc.setFillColor(...COLOR_TITLE_BG);
  doc.setDrawColor(...COLOR_BORDER);
  doc.setLineWidth(0.3);
  doc.rect(margin, y, pageW - margin * 2, 12, "FD");
  doc.setTextColor(...COLOR_TEXT);
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(12);
  const empresaNombre = empresa.razon_social || empresa.nombre || "Empresa";
  doc.text(empresaNombre, pageW / 2, y + 8, { align: "center" });
  y += 14;

  // Logo (esquina superior derecha, si existe)
  const logoFmt = detectImageFormat(empresa.logo);
  if (logoFmt) {
    try { doc.addImage(empresa.logo, logoFmt, pageW - margin - 22, y, 20, 22); } catch (e) { /* ignore */ }
  }

  // === Título ===
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(11);
  doc.setTextColor(...COLOR_ACCENT);
  doc.text("COMPROBANTE DE VACACIONES PAGADAS", margin, y + 5);
  y += 8;

  doc.setFont(FONT_FAMILY, "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...COLOR_MUTED);
  doc.text(`Fecha del pago: ${fmtFechaLarga(fechaPago)}`, margin, y + 4);
  y += 10;

  // === Datos empleado (ancho completo) ===
  const rowH = 6.5;
  const labelW = 34;
  const valueW = pageW - margin * 2 - labelW;
  const empRows = [
    ["Trabajador:", `${empleado.nombre || ""} ${empleado.apellidos || ""}`.trim() || "—"],
    ["Cédula:", empleado.cedula || "—"],
    ["Puesto:", empleado.puesto || "—"],
    ["Departamento:", empleado.departamento || "—"],
    ["Fecha ingreso:", empleado.fecha_ingreso ? fmtFechaLarga(empleado.fecha_ingreso) : "—"],
  ];
  empRows.forEach((r, i) => {
    const yy = y + i * rowH;
    doc.setFillColor(...COLOR_LABEL_BG); doc.rect(margin, yy, labelW, rowH, "FD");
    doc.setFillColor(...COLOR_VALUE_BG); doc.rect(margin + labelW, yy, valueW, rowH, "FD");
    doc.setFont(FONT_FAMILY, "bold"); doc.setFontSize(8.5); doc.setTextColor(...COLOR_TEXT);
    doc.text(r[0], margin + 1.5, yy + 4.4);
    doc.setFont(FONT_FAMILY, "normal");
    doc.text(String(r[1] || "—"), margin + labelW + 2, yy + 4.4);
  });
  y += empRows.length * rowH + 8;

  // === Cards resumen (2 columnas): Días pagados / Monto ===
  const cardW = (pageW - margin * 2 - 4) / 2;
  const cardH = 22;
  // Card izquierda: Días
  doc.setFillColor(...COLOR_LABEL_BG);
  doc.rect(margin, y, cardW, 8, "FD");
  doc.setFont(FONT_FAMILY, "bold"); doc.setFontSize(9); doc.setTextColor(...COLOR_TEXT);
  doc.text("Días de vacaciones pagados", margin + cardW / 2, y + 5.5, { align: "center" });
  doc.setFillColor(...COLOR_VALUE_BG);
  doc.rect(margin, y + 8, cardW, cardH - 8, "FD");
  doc.setFont(FONT_FAMILY, "bold"); doc.setFontSize(16); doc.setTextColor(...COLOR_ACCENT);
  doc.text(`${diasPagados} ${Number(diasPagados) === 1 ? "día" : "días"}`, margin + cardW / 2, y + 17.5, { align: "center" });

  // Card derecha: Monto
  const cardMX = margin + cardW + 4;
  doc.setFillColor(...COLOR_LABEL_BG);
  doc.rect(cardMX, y, cardW, 8, "FD");
  doc.setFont(FONT_FAMILY, "bold"); doc.setFontSize(9); doc.setTextColor(...COLOR_TEXT);
  doc.text("Monto pagado", cardMX + cardW / 2, y + 5.5, { align: "center" });
  doc.setFillColor(...COLOR_VALUE_BG);
  doc.rect(cardMX, y + 8, cardW, cardH - 8, "FD");
  doc.setFont(FONT_FAMILY, "bold"); doc.setFontSize(14); doc.setTextColor(...COLOR_ACCENT);
  doc.text(fmtCRC(monto), cardMX + cardW / 2, y + 17.5, { align: "center" });
  y += cardH + 6;

  // === Motivo / Observaciones ===
  if (motivo) {
    doc.setFillColor(...COLOR_LABEL_BG);
    doc.rect(margin, y, pageW - margin * 2, 6, "FD");
    doc.setFont(FONT_FAMILY, "bold"); doc.setFontSize(8.5); doc.setTextColor(...COLOR_TEXT);
    doc.text("Observaciones", margin + 2, y + 4.2);
    y += 6;
    doc.setFillColor(...COLOR_VALUE_BG);
    doc.rect(margin, y, pageW - margin * 2, 10, "FD");
    doc.setFont(FONT_FAMILY, "normal"); doc.setFontSize(8.5);
    const lines = doc.splitTextToSize(motivo, pageW - margin * 2 - 4);
    doc.text(lines.slice(0, 2), margin + 2, y + 4);
    y += 12;
  }

  // === Nota legal breve ===
  doc.setFont(FONT_FAMILY, "normal"); doc.setFontSize(7.5); doc.setTextColor(...COLOR_MUTED);
  const nota = `De acuerdo con lo convenido entre ambas partes, ${empresaNombre} realiza el pago compensatorio de ${diasPagados} ${Number(diasPagados) === 1 ? "día" : "días"} de vacaciones al trabajador ${empleado.nombre || ""} ${empleado.apellidos || ""}, por un monto total de ${fmtCRC(monto)}. Con la firma del presente comprobante, el trabajador confirma la recepción a satisfacción del monto indicado.`;
  const notaLines = doc.splitTextToSize(nota, pageW - margin * 2);
  doc.text(notaLines, margin, y + 4);
  y += notaLines.length * 3.4 + 8;

  // === Firmas ===
  const firmasY = Math.min(y + 14, pageH - 22);
  doc.setDrawColor(...COLOR_BORDER); doc.setLineWidth(0.4);
  const firmaW = 44;
  const f1X = pageW * 0.28 - firmaW / 2;
  const f2X = pageW * 0.72 - firmaW / 2;
  doc.line(f1X, firmasY, f1X + firmaW, firmasY);
  doc.line(f2X, firmasY, f2X + firmaW, firmasY);
  doc.setTextColor(...COLOR_TEXT);
  doc.setFont(FONT_FAMILY, "bold"); doc.setFontSize(8);
  doc.text(empresaNombre, pageW * 0.28, firmasY + 3.8, { align: "center" });
  doc.text(`${empleado.nombre || ""} ${empleado.apellidos || ""}`.trim() || "—", pageW * 0.72, firmasY + 3.8, { align: "center" });
  doc.setFont(FONT_FAMILY, "normal"); doc.setFontSize(7); doc.setTextColor(...COLOR_MUTED);
  doc.text("Firma Encargado Patronal", pageW * 0.28, firmasY + 7.5, { align: "center" });
  doc.text("Firma del Trabajador", pageW * 0.72, firmasY + 7.5, { align: "center" });

  // Pie
  doc.setFont(FONT_FAMILY, "normal"); doc.setFontSize(6.5); doc.setTextColor(...COLOR_MUTED);
  const fechaGen = new Date().toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" });
  doc.text(`Generado el ${fechaGen} · ${empresaNombre}`, pageW - margin, pageH - 5, { align: "right" });

  const safe = `${empleado.nombre || ""}_${empleado.apellidos || ""}`.replace(/\s+/g, "_");
  const fileName = `Vacaciones_Pagadas_${fechaPago || "sin_fecha"}_${safe}.pdf`;
  doc.save(fileName);
  const dataUri = doc.output("datauristring");
  return { fileName, pdfBase64: dataUri.split(",")[1] };
}
